import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from './toast.service';
import { ApiError, ApiErrorHandlerService } from './api-error-handler.service';

type ApiValidationError = {
    property?: string;
    constraints?: Record<string, string>;
};

interface UserUpdateErrorBody {
    ValidationErrors?: ApiValidationError[];
    message?: string;
    error?: ApiError;
}

const PROPERTY_LABEL_KEYS: Record<string, string> = {
    email: 'user_email',
    firstName: 'user_firstname',
    lastName: 'user_lastname',
};

/**
 * Common validation-error messages for members', profile and onboarding.
 * Shows translated validation feedback for user creation and updates or fallback, skipping API errors
 * already handled globally to prevent duplicate toasts.
 */
@Injectable({ providedIn: 'root' })
export class UserUpdateErrorService {
    constructor(
        private readonly translate: TranslateService,
        private readonly toast: ToastService,
        private readonly apiErrorHandler: ApiErrorHandlerService,
    ) {}

    show(err: HttpErrorResponse, fallbackDetailKey: string): void {
        const body = err.error as UserUpdateErrorBody | undefined;
        const errors = body?.ValidationErrors;
        if (errors?.length) {
            const first = errors[0];
            const constraint = first.constraints ? Object.keys(first.constraints)[0] : '';
            if (!this.showValidationError(constraint, first.property ?? '')) {
                console.error(err);
                this.toast.error('standard_error', fallbackDetailKey);
            }
        } else if (!body?.error || !this.apiErrorHandler.canHandle(body.error)) {
            if (err.status === 400 && body?.message === 'Maximum number of licenses exceeded') {
                this.toast.error('user_licenseErrorHeader', 'user_licenseErrorBody');
            } else {
                this.toast.error('standard_error', fallbackDetailKey);
            }
        }
    }

    // note: members reuses this message in its defensive empty-name check inside updateUser(),
    // although the Save button normally prevents submitting an empty name to begin with.
    showValidationError(constraint: string, property: string): boolean {
        const labelKey = PROPERTY_LABEL_KEYS[property];
        const field = labelKey ? this.translate.instant(labelKey) : property;
        const titleKey = 'validationErrors.' + constraint + '_title';
        const title = this.translate.instant(titleKey, { field });
        if (title === '' || title === titleKey) {
            return false;
        }
        this.toast.errorNoTranslate(title, this.translate.instant('validationErrors.' + constraint + '_message'));
        return true;
    }
}
